import { cancel, select, takeEvery } from 'redux-saga/effects';
import {
  DruckRequestPayload,
  getGeschaeftsart,
  GlobalAction,
  GlobalActions,
  GlobalPut,
} from '@ps20-frontend/shop-integration-redux';
import {
  App,
  mapLeistungen,
  mapToBffVorvertragsdatenDruck,
  mapZusatzoptionenDruck,
  numberToGermanNumberFormattedString,
} from '@ps20-frontend/frontend-models';
import {
  Feature,
  PhDruckdaten,
  SpartenType,
  VorschaedenDruck,
  ZusatzoptionenDruck,
} from '@ps20/bff-models';
import { PS20Logger } from '@ps20-frontend/logging';
import { getProduktlinienDruck, mapGefahren, mapLinienpreis } from '@ps20-frontend/druckdaten-lib';
import { safeSaga } from '@ps20-frontend/safe-saga';
import { mapGesamtbeitragIfJaehrlichAndBeitragIsWrong } from '@ps20-frontend/preis-lib';
import { selectFeatureToggle } from '@ps20-frontend/selector';
import {
  selectSelectedProductZusatzoptionItems,
  selectSelectedProduktlinie,
} from '../../app/shared/selectors/produkt.selector';
import { IAppState } from '../appState';
import { Vorschaeden } from '../reducers/vorschaeden.reducer';
import { selectVorschaeden } from '../../app/shared/selectors/vorschaeden.selector';
import { selectAnzahlWohnungen } from '../../app/shared/selectors/anzahlWohnungen.selector';

export function* watchDruckdatenChanged() {
  yield takeEvery(GlobalActions.DRUCKDATEN_REQUESTED, safeSaga(onDruckdatenRequested, App.PH));
}

export function* onDruckdatenRequested(action: GlobalAction<DruckRequestPayload>) {
  try {
    const state: IAppState = yield select();
    const appIds: string[] = action.payload.appIds;

    if (!appIds.includes(App.PH)) {
      yield cancel();
      return;
    }

    const phDruckdaten: PhDruckdaten = mapDruckdaten(state);
    GlobalPut.getInstance().put(new GlobalActions().druckdatenGenerated(phDruckdaten));
  } catch (e) {
    PS20Logger.error(App.PH, e as Error, 'Druckdatensaga.onDruckdatenRequested()');
    yield cancel();
  }
}

function mapDruckdaten(state: IAppState): PhDruckdaten {
  const vorschaeden: Vorschaeden = selectVorschaeden(state);
  const zusatzoptionenDruck: Array<ZusatzoptionenDruck> = mapZusatzoptionenDruck(
    selectSelectedProductZusatzoptionItems(state),
  );
  const ausgewaehlteProduktlinie = selectSelectedProduktlinie(state);
  const vorteilsRabattActivated = selectFeatureToggle(state, Feature.VORTEILSRABATT);
  return {
    antragsnummer: state.antragsnummer,
    longText: ausgewaehlteProduktlinie?.langtext,
    shortText: ausgewaehlteProduktlinie.kurztext,
    rbdAktuell: state.taasInfo.rbdNachlass,
    vorteilsRabatt: vorteilsRabattActivated ? state.taasInfo.vorteilsrabatt || 0 : undefined,
    angebotsdatum: vorteilsRabattActivated ? state.vertragsoptionen.angebotsdatum : undefined,
    zaehlstuecke: state.rabattstufe,
    treuebonus: state.taasInfo.tbNachlass,
    spartenType: SpartenType.PRIVATHAFTPFLICHT,
    beginn: null, // kommt vom Checkout
    ablauf: null, // kommt vom Checkout
    gesamtbeitrag: numberToGermanNumberFormattedString(
      mapGesamtbeitragIfJaehrlichAndBeitragIsWrong(
        state.vertragsoptionen.zahlungsweise,
        state.gesamtbeitragMitRabatt,
        state.jahresbeitrag,
        App.PH,
      ),
    ),
    selbstbeteiligung: state.vertragsoptionen.selbstbeteiligung,
    vertragsdauer: null,
    linienpreis: mapLinienpreis(state.produkt),
    leistungen: mapLeistungen(ausgewaehlteProduktlinie, SpartenType.PRIVATHAFTPFLICHT),
    zusatzoptionenDruck,
    vorschaedenDruck: mapVorschaeden(vorschaeden),
    vorversicherungenDruck: null,
    gefahrenDruck: mapGefahren(state.produkt),
    produktlinien: getProduktlinienDruck(state.produkt),
    zuersGefaehrdungsklasse: '',
    vorvertragsnummern: mapToBffVorvertragsdatenDruck(state.vertragsoptionen.vorvertragsNummern),
    vorvertragGekuendigt: state.vertragsoptionen.vorvertragGekuendigt,
    abweichungVomZielmarkt: false,
    beibehalten: state.vertragsoptionen.beibehalten,
    anzahlWohnungen: selectAnzahlWohnungen(state),
    familienstand: state.versicherungsnehmer.familienstand,
    plzVersicherungsnehmer: state.risikoort.plz,
    vermieteteWohnungDruckList: [],
    nachlass: ausgewaehlteProduktlinie.nachlass,
    kooperation: state.vertragsoptionen.selectedKooperation,
    geschaeftsart: getGeschaeftsart(state.vertragsoptionen),
  };
}

function mapVorschaeden(vorschaden: Vorschaeden): VorschaedenDruck {
  return {
    vorhanden: true,
    haftpflicht: vorschaden.phVorschaeden,
  };
}
